import React from "react";
import "./OneTimePasscode.scss";

import { Helmet } from "react-helmet";
import { Button } from "@thrivetribe/gloji.ui.atoms.button";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import PageBg from "../../../assets/img/bg/eligibilityCheckerBg.png";
import {
  ModalPageBackground,
  ModalPageBody,
  ModalPageWrapper,
  ModalPageCopy,
  ModalPageHeading,
  ModalPageHeader,
} from "../../organisms/ModalPage";
import Toastify from "../../atoms/Toastify";

import { ReactComponent as Logo } from "../../../assets/img/logo.svg";
import TextField from "../../atoms/react-hook-form/TextField/TextField";
import { useVerifyOtpMutation, useRequestOtpMutation } from "../../../redux/profileApiV3";
import { saveEmailVerified } from "../../../redux/slices/otpSlice";
import { verifyOtpSchema } from "./formValidation";

interface OneTimePasscodeVerifyProps {}

interface OtpVerifyValues {
  email?: string;
  otp?: string;
}

export const OneTimePasscodeVerify: React.FC<OneTimePasscodeVerifyProps> = (
  props: OneTimePasscodeVerifyProps
) => {
  const [verifyOtp] = useVerifyOtpMutation();
  const [requestOtp] = useRequestOtpMutation();
  const dispatch = useDispatch();
  const { email } = useParams();
  const history = useHistory();

  const { handleSubmit, getValues, control, setError } = useForm<OtpVerifyValues>({
    resolver: yupResolver(verifyOtpSchema),
    defaultValues: { email: email, otp: "" },
  });

  const onRequestOtp = values => {
    requestOtp(values.email)
      .unwrap()
      .then(() => {
        Toastify.success(`We've sent an email to ${values.email}`);
      })
      .catch(rejected => {
        Toastify.error(`Request failed. Please try again: ${rejected}`);
        console.error(rejected);
      });
  };

  const onSubmit = async values => {
    verifyOtp(values)
      .unwrap()
      .then(async response => {
        if (response.isValid === false) {
          setError("otp", {
            type: "manual",
            message: "Incorrect code. Try again or click the button below to request a new code.",
          });
        } else {
          dispatch(saveEmailVerified({ email: values.email, isVerified: true }));
          history.push("/select-programme");
        }
      })
      .catch(rejected => {
        Toastify.error("Code entered is invalid please try again.");
        console.error(rejected);
      });
  };

  return (
    <ModalPageBackground className="otp-page" bgImage={PageBg}>
      <Helmet>
        <title>Lets team up | Otp | Sign up | Gloji</title>
      </Helmet>
      <ModalPageWrapper className="otp-email" alignItems="center">
        <ModalPageBody hasStickyFooter={false}>
          <>
            <ModalPageHeader
              backButtonOnClick={() => {
                window.location.href = "https://www.gloji.co.uk";
              }}
              backButtonText="Cancel"
            >
              <Logo id="glojiHeaderLogo" />
            </ModalPageHeader>
            <ModalPageHeading size="large">Let’s check your code</ModalPageHeading>
            <ModalPageCopy>Enter the code we sent to your email address.</ModalPageCopy>
            <ModalPageCopy>Can’t see it? Might be hiding in your spam folder.</ModalPageCopy>
          </>
          <form onSubmit={handleSubmit(onSubmit)} className="form email">
            <div className="main-page__content">
              <div className="form__body">
                <TextField label="Email address" name="email" control={control} disabled={true} />
                <TextField label="Code" name="otp" control={control} />
              </div>
            </div>
            <Button type="submit" variant="contained" color="primary">
              Verify code
            </Button>
          </form>
          <button
            aria-label="request one time passcode"
            className="underline-link secondary"
            onClick={() => onRequestOtp(getValues())}
            // onKeyUp={() => onRequestOtp(getValues())}
          >
            Send new code
          </button>
        </ModalPageBody>
      </ModalPageWrapper>
    </ModalPageBackground>
  );
};
